import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaPlay, FaStop, FaEye } from 'react-icons/fa';

const LivestreamAdmin = () => {
  const [livestreams, setLivestreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLivestreams = async () => {
      try {
        const response = await fetch('https://localhost:7176/api/Livestream');
        if (response.ok) {
          const data = await response.json();
          setLivestreams(data);
        } else {
          setError('Error al cargar los livestreams');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al conectar con el servidor');
        setLoading(false);
      }
    };

    fetchLivestreams();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await fetch(`https://localhost:7176/api/Livestream/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        // Actualizar livestreams localmente
        setLivestreams(livestreams.map(l => 
          l.livestreamId === id ? { ...l, estado: newStatus } : l
        ));
      } else {
        setError('Error al actualizar el estado');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error al conectar con el servidor');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este livestream?')) return;

    try {
      const response = await fetch(`https://localhost:7176/api/Livestream/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        // Eliminar de la lista local
        setLivestreams(livestreams.filter(l => l.livestreamId !== id));
      } else {
        setError('Error al eliminar el livestream');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Error al conectar con el servidor');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-md">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Administración de Livestreams</h1>
        <Link 
          to="/admin/livestreams/new" 
          className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded-md flex items-center"
        >
          <FaPlus className="mr-2" /> Nuevo Livestream
        </Link>
      </div>

      {livestreams.length === 0 ? (
        <div className="text-center p-10 bg-gray-100 rounded-md">
          <p className="text-gray-600">No hay livestreams disponibles</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg overflow-hidden shadow-lg">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="py-3 px-4 text-left">Título</th>
                <th className="py-3 px-4 text-left">Fecha</th>
                <th className="py-3 px-4 text-left">Estado</th>
                <th className="py-3 px-4 text-left">Vistas</th>
                <th className="py-3 px-4 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {livestreams.map(livestream => (
                <tr key={livestream.livestreamId} className="hover:bg-gray-50">
                  <td className="py-3 px-4">{livestream.titulo}</td>
                  <td className="py-3 px-4">{new Date(livestream.fecha).toLocaleString()}</td>
                  <td className="py-3 px-4">
                    <span 
                      className={`px-2 py-1 rounded-full text-xs ${
                        livestream.estado === 'En Vivo' 
                          ? 'bg-red-100 text-red-700' 
                          : livestream.estado === 'Próximo' 
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {livestream.estado}
                    </span>
                  </td>
                  <td className="py-3 px-4">{livestream.vistas}</td>
                  <td className="py-3 px-4 flex space-x-2">
                    <Link 
                      to={`/admin/livestreams/edit/${livestream.livestreamId}`}
                      className="text-blue-600 hover:text-blue-800"
                      title="Editar"
                    >
                      <FaEdit />
                    </Link>
                    
                    <button 
                      onClick={() => handleDelete(livestream.livestreamId)}
                      className="text-red-600 hover:text-red-800"
                      title="Eliminar"
                    >
                      <FaTrash />
                    </button>
                    
                    <Link 
                      to={`/livestream/${livestream.livestreamId}`}
                      className="text-green-600 hover:text-green-800"
                      title="Ver"
                    >
                      <FaEye />
                    </Link>
                    
                    {livestream.estado !== 'En Vivo' ? (
                      <button 
                        onClick={() => handleStatusChange(livestream.livestreamId, 'En Vivo')}
                        className="text-purple-600 hover:text-purple-800"
                        title="Iniciar transmisión"
                      >
                        <FaPlay />
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleStatusChange(livestream.livestreamId, 'Finalizado')}
                        className="text-orange-600 hover:text-orange-800"
                        title="Finalizar transmisión"
                      >
                        <FaStop />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LivestreamAdmin;